import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CheckCircle2, Building2, Users, GraduationCap, Briefcase, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function B2BPage() {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-4">
                <Building2 className="w-8 h-8 mr-3 text-[#CE1126]" />
                <span className="text-sm font-medium text-gray-300">FOR TRAINING CENTERS</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight">
                Empower Your Students with{' '}
                <span className="text-[#CE1126]">UAE Job-Ready Skills</span>
              </h1>
              <p className="text-lg text-gray-300 mb-8">
                Daily UAE Jobs helps training institutes, universities, and recruitment agencies 
                prepare candidates for the UAE job market at scale.
              </p>
              <Link to="/contact">
                <Button size="lg" className="btn-uae-red text-lg px-8">
                  Contact Sales
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Card className="p-6 bg-white bg-opacity-10 backdrop-blur">
                <Users className="w-10 h-10 text-[#CE1126] mb-3" />
                <h3 className="text-xl font-bold">500+</h3>
                <p className="text-sm text-gray-300">Job seekers trained</p>
              </Card>
              <Card className="p-6 bg-white bg-opacity-10 backdrop-blur">
                <GraduationCap className="w-10 h-10 text-[#009639] mb-3" />
                <h3 className="text-xl font-bold">20+</h3>
                <p className="text-sm text-gray-300">Training partners</p>
              </Card>
              <Card className="p-6 bg-white bg-opacity-10 backdrop-blur">
                <Briefcase className="w-10 h-10 text-blue-400 mb-3" />
                <h3 className="text-xl font-bold">85%</h3>
                <p className="text-sm text-gray-300">Interview success rate</p>
              </Card>
              <Card className="p-6 bg-white bg-opacity-10 backdrop-blur">
                <CheckCircle2 className="w-10 h-10 text-yellow-400 mb-3" />
                <h3 className="text-xl font-bold">UAE</h3>
                <p className="text-sm text-gray-300">Market optimized</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-4">What Your Students Get</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Comprehensive career tools designed for the UAE job market
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6">
              <div className="w-14 h-14 rounded-full bg-[#CE1126] bg-opacity-10 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-7 h-7 text-[#CE1126]" />
              </div>
              <h3 className="text-xl font-bold mb-3">ATS CV Checker</h3>
              <p className="text-gray-600">
                Ensure every CV passes ATS filters with detailed scoring and improvement suggestions.
              </p>
            </Card>
            <Card className="p-6">
              <div className="w-14 h-14 rounded-full bg-[#009639] bg-opacity-10 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-7 h-7 text-[#009639]" />
              </div>
              <h3 className="text-xl font-bold mb-3">CV Fix Service</h3>
              <p className="text-gray-600">
                AI-powered CV improvements that help candidates present their best professional self.
              </p>
            </Card>
            <Card className="p-6">
              <div className="w-14 h-14 rounded-full bg-blue-500 bg-opacity-10 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-7 h-7 text-blue-500" />
              </div>
              <h3 className="text-xl font-bold mb-3">Interview Booster</h3>
              <p className="text-gray-600">
                English interview preparation tailored for UAE employers and hiring standards.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-4">B2B Pricing</h2>
          <p className="text-center text-gray-600 mb-12">
            Bulk pricing for training institutes and recruitment agencies
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-8 border-2 border-gray-200 hover:border-[#CE1126] transition-colors">
              <h3 className="text-xl font-bold mb-2">Starter</h3>
              <div className="mb-4">
                <span className="text-4xl font-bold">AED 3,000</span>
                <span className="text-gray-600"> / month</span>
              </div>
              <p className="text-[#CE1126] font-medium mb-6">50 candidates</p>
              <ul className="space-y-3 mb-8 text-sm">
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                  <span>Full ATS CV checker access</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                  <span>Interview Booster included</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                  <span>Admin dashboard</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                  <span>Email support</span>
                </li>
              </ul>
              <Link to="/contact">
                <Button variant="outline" className="w-full">Contact Sales</Button>
              </Link>
            </Card>

            <Card className="p-8 border-2 border-[#CE1126] shadow-xl relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#CE1126] text-white px-4 py-1 rounded-full text-sm font-medium">
                Most Popular
              </div>
              <h3 className="text-xl font-bold mb-2">Growth</h3>
              <div className="mb-4">
                <span className="text-4xl font-bold">AED 5,000</span>
                <span className="text-gray-600"> / month</span>
              </div>
              <p className="text-[#CE1126] font-medium mb-6">100 candidates</p>
              <ul className="space-y-3 mb-8 text-sm">
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                  <span>Everything in Starter</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                  <span>CV Fix service included</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                  <span>Priority support</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                  <span>Progress reports</span>
                </li>
              </ul>
              <Link to="/contact">
                <Button className="w-full btn-uae-red">Contact Sales</Button>
              </Link>
            </Card>

            <Card className="p-8 border-2 border-gray-200 hover:border-[#CE1126] transition-colors">
              <h3 className="text-xl font-bold mb-2">Enterprise</h3>
              <div className="mb-4">
                <span className="text-4xl font-bold">Custom</span>
              </div>
              <p className="text-[#CE1126] font-medium mb-6">300+ candidates</p>
              <ul className="space-y-3 mb-8 text-sm">
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                  <span>Everything in Growth</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                  <span>Custom branding</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                  <span>Dedicated account manager</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                  <span>API access</span>
                </li>
              </ul>
              <Link to="/contact">
                <Button variant="outline" className="w-full">Contact Sales</Button>
              </Link>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Empower Your Students?</h2>
          <p className="text-lg text-gray-600 mb-8">
            Join 20+ training centers already using Daily UAE Jobs to prepare candidates for the UAE job market.
          </p>
          <Link to="/contact">
            <Button size="lg" className="btn-uae-red text-lg px-10">
              Contact Sales
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
